/**
 * The seats, laid out as on the old kiosk: the coaches on the left, the coach drawn lying down in the
 * middle (a column per seat row, the aisle as a gap), the legend on the right, coach paging below.
 *
 * The coach shown is the backend's (`page_idx`): a tap on another coach asks the backend to show it, so
 * voice and touch agree. Picked seats stay in the screen draft until "Next" sends them; until then the
 * backend's auto-assigned seats are shown as the user's.
 */
import { z } from 'zod';
import { Icon } from '@platform/ui-kit/Icon';
import { Panel } from '@platform/ui-kit/Panel';
import { defineWidget } from '@platform/widgets/contract';
import { coachGrid, togglePick } from '../seats';
import { coachSchema } from '../session';
import css from './Trains.module.css';

const schema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  coaches: z.array(coachSchema),
  page: z.number().int().min(0).default(0),
  needed: z.number().int().min(1),
  assigned: z.array(z.object({ seat: z.string(), coach: z.string() })).default([]),
  destination: z.string().optional(),
});

/** The draft this widget writes: the seats picked and their coach. */
export interface SeatDraft {
  seats?: string[];
  coach?: string;
}

export const seatMap = defineWidget({
  key: 'seat-map',
  schema,
  Component: function SeatMap({ props, ctx, api }) {
    const { t } = ctx;
    const index = Math.min(props.page, props.coaches.length - 1);
    const coach = props.coaches[index];
    if (!coach) return <Panel title={props.title}><p className={css.noTrains}>{t('trains.seats.none')}</p></Panel>;
    const grid = coachGrid(coach);
    const draft = ctx.draft as SeatDraft;
    const coachId = String(coach.coach_number);
    const assignedHere = props.assigned.filter((a) => a.coach === coachId).map((a) => a.seat);
    // Picked on screen wins; picked on another coach means none here; nothing picked shows the backend's.
    const picked = draft.coach === coachId ? draft.seats ?? [] : [];
    const mine = draft.seats?.length ? picked : assignedHere;
    const pick = (label: string) => {
      if (api.readOnly) return;
      const base = draft.coach === coachId ? draft.seats ?? [] : [];
      api.setDraft({ seats: togglePick(base, label, props.needed), coach: coachId });
    };
    const show = (i: number) => {
      const target = props.coaches[i];
      if (target && i !== index) api.press('go_next_page', { coach: String(target.coach_number) });
    };

    return (
      <Panel title={props.title} subtitle={props.subtitle} icon="seat"
        aside={<span className={css.coachChip}>{t('trains.seats.coach', { n: coach.coach_number })} · {grid.className}</span>}>
        <div className={css.seatLayout}>
          <nav className={css.coachList} aria-label={t('trains.seats.coaches')}>
            <span className={css.columnTitle}>{t('trains.seats.coaches')}</span>
            {props.coaches.map((c, i) => (
              <button key={c.coach_number} className={`${css.coachTab} ${i === index ? css.coachActive : ''}`} onClick={() => show(i)}>
                <Icon name="train" size={20} />
                <strong>{t('trains.seats.coach_short', { n: c.coach_number })}</strong>
                <span>{c.display_class || c.class_name}</span>
              </button>
            ))}
          </nav>
          <div className={css.coachBody}>
            {props.destination && <span className={css.direction}><Icon name="next" size={16} />{props.destination}</span>}
            <div className={css.seatGrid}>
              {Array.from({ length: grid.width }, (_, line) => (
                <div key={line} className={css.seatLine}>
                  {grid.aisleAfter > 0 && line === grid.aisleAfter && <span className={css.aisle}>{t('trains.seats.aisle_label')}</span>}
                  <div className={css.seatCells}>
                    {grid.rows.map((row, r) => {
                      const cell = row[line];
                      if (!cell) return <span key={r} className={css.gap} />;
                      const isMine = mine.includes(cell.label);
                      return (
                        <button key={r} className={`${css.seat} ${cell.occupied ? css.taken : ''} ${isMine ? css.mine : ''}`}
                          disabled={cell.occupied && !isMine} onClick={() => pick(cell.label)} aria-pressed={isMine}
                          aria-label={cell.position === 'middle' ? cell.label : `${cell.label} ${t(`trains.seats.${cell.position}`)}`}>
                          {cell.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <aside className={css.legend}>
            <span className={css.columnTitle}>{t('trains.seats.legend')}</span>
            <span><i className={css.swatch} />{t('trains.seats.free')}</span>
            <span><i className={`${css.swatch} ${css.taken}`} />{t('trains.seats.taken')}</span>
            <span><i className={`${css.swatch} ${css.mine}`} />{t('trains.seats.yours')}</span>
            <strong className={css.pickCount}>{t('trains.seats.pick', { n: mine.length, total: props.needed })}</strong>
            {!draft.seats?.length && assignedHere.length > 0 && <span className={css.auto}>{t('trains.seats.auto', { seats: assignedHere.join(', ') })}</span>}
          </aside>
        </div>
        {props.coaches.length > 1 && (
          <div className={css.coachPager}>
            <button className={css.pagerButton} disabled={index === 0} onClick={() => show(index - 1)}>← {t('trains.action.prev_page')}</button>
            <span className={css.pagerDots}>{props.coaches.map((c, i) => <i key={c.coach_number} className={i === index ? css.dotOn : undefined} />)}</span>
            <button className={`${css.pagerButton} ${css.pagerNext}`} disabled={index >= props.coaches.length - 1} onClick={() => show(index + 1)}>{t('trains.action.next_page')} →</button>
          </div>
        )}
      </Panel>
    );
  },
});
